import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  Card,
  Input,
  Select,
  Tag,
  Typography,
  Spin,
  message,
  Row,
  Col,
  Statistic,
  Space,
  Button,
  Tooltip,
  Progress,
  Empty
} from 'antd';
import {
  SearchOutlined,
  DatabaseOutlined,
  TagsOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

const ScrapedProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchMode, setSearchMode] = useState('text'); // 'text' or 'semantic'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [semanticResults, setSemanticResults] = useState(null);

  // Initialize from URL params
  useEffect(() => {
    const vendorParam = searchParams.get('vendor');
    if (vendorParam) {
      const vendorId = parseInt(vendorParam, 10);
      if (!isNaN(vendorId)) {
        setSelectedVendor(vendorId);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    fetchStats();
  }, []);

  // Fetch products when selectedVendor changes (including from URL param)
  useEffect(() => {
    fetchProducts(selectedVendor, selectedCategory, searchQuery);
  }, [selectedVendor]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/scraping/stats/public`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchProducts = async (vendorId = null, category = null, search = null) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (vendorId) params.append('vendor_id', vendorId);
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      params.append('limit', '200');

      const response = await axios.get(
        `${API_BASE_URL}/api/scraping/products/scraped/public?${params.toString()}`
      );
      setProducts(response.data);
      setSemanticResults(null);
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleSemanticSearch = async (query) => {
    if (!query.trim()) {
      message.warning('Please enter a search query');
      return;
    }

    try {
      setSearchLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/scraping/search/semantic/public`,
        {
          query: query,
          limit: 20,
          min_similarity: 0.3,
          vendor_id: selectedVendor,
          category: selectedCategory
        }
      );
      setSemanticResults(response.data);
      message.success(`Found ${response.data.length} semantically similar products`);
    } catch (error) {
      console.error('Error performing semantic search:', error);
      message.error('Semantic search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    if (searchMode === 'semantic' && value) {
      handleSemanticSearch(value);
    } else {
      fetchProducts(selectedVendor, selectedCategory, value);
    }
  };

  const handleVendorChange = (value) => {
    setSelectedVendor(value);
    setSemanticResults(null);
    // Update URL param
    if (value) {
      setSearchParams({ vendor: value });
    } else {
      searchParams.delete('vendor');
      setSearchParams(searchParams);
    }
    fetchProducts(value, selectedCategory, searchQuery);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setSemanticResults(null);
    fetchProducts(selectedVendor, value, searchQuery);
  };

  const handleReset = () => {
    setSearchQuery('');
    setSelectedVendor(null);
    setSelectedCategory(null);
    setSemanticResults(null);
    setSearchParams({});  // Clear URL params
    fetchProducts();
  };

  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.similarity_score && (
            <div style={{ marginTop: 4 }}>
              <Progress
                percent={Math.round(record.similarity_score * 100)}
                size="small"
                status="active"
                format={(percent) => `${percent}% match`}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>,
      sorter: (a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (text) => text ? <Tag color="green">{text}</Tag> : <Text type="secondary">N/A</Text>,
      sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 450,
      render: (text) => (
        <Paragraph
          ellipsis={{
            rows: 2,
            expandable: 'collapsible',
            symbol: (expanded) => expanded ? 'Show less' : 'Show more'
          }}
          style={{ marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {text || 'No description available'}
        </Paragraph>
      ),
    },
    {
      title: 'Features',
      dataIndex: 'features',
      key: 'features',
      width: 200,
      render: (features) => {
        if (!features || features.length === 0) return <Text type="secondary">-</Text>;
        return (
          <Space size={[0, 4]} wrap>
            {features.slice(0, 3).map((feature, idx) => (
              <Tag key={idx} style={{ fontSize: '11px' }}>{feature}</Tag>
            ))}
            {features.length > 3 && (
              <Tooltip title={features.slice(3).join(', ')}>
                <Tag>+{features.length - 3} more</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  // For semantic search results, use similarity_score
  const semanticColumns = columns.map(col => {
    if (col.key === 'name') {
      return {
        ...col,
        render: (text, record) => (
          <div>
            <Text strong>{text}</Text>
            <div style={{ marginTop: 4 }}>
              <Progress
                percent={Math.round(record.similarity_score * 100)}
                size="small"
                status="active"
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                format={(percent) => `${percent}% match`}
              />
            </div>
          </div>
        ),
      };
    }
    return col;
  });

  const displayData = semanticResults || products;
  const displayColumns = semanticResults ? semanticColumns : columns;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 0 }}>
          <DatabaseOutlined style={{ marginRight: 12 }} />
          Vendor Product Database
        </Title>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>
          Reset Filters
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Vendors"
                value={stats.total_vendors}
                prefix={<ShopOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Vendors with Products"
                value={stats.vendors_with_products}
                prefix={<ShopOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Products"
                value={stats.total_scraped_products}
                prefix={<TagsOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Product Embeddings"
                value={stats.total_embeddings}
                prefix={<ThunderboltOutlined />}
                suffix={<Text type="secondary" style={{ fontSize: 12 }}>(for AI search)</Text>}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Search and Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Search
              placeholder={searchMode === 'semantic'
                ? "Describe what you're looking for..."
                : "Search products by name or description..."
              }
              allowClear
              enterButton={searchMode === 'semantic' ? <><ThunderboltOutlined /> AI Search</> : <><SearchOutlined /> Search</>}
              size="large"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={handleSearch}
              loading={searchLoading}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Search Mode"
              style={{ width: '100%' }}
              size="large"
              value={searchMode}
              onChange={(value) => {
                setSearchMode(value);
                if (value === 'text') {
                  setSemanticResults(null);
                }
              }}
            >
              <Option value="text">
                <SearchOutlined /> Text Search
              </Option>
              <Option value="semantic">
                <ThunderboltOutlined /> AI Semantic Search
              </Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="Filter by Vendor"
              style={{ width: '100%' }}
              size="large"
              allowClear
              value={selectedVendor}
              onChange={handleVendorChange}
            >
              {stats && stats.vendors
                .filter(v => v.count > 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(vendor => (
                  <Option key={vendor.id} value={vendor.id}>
                    {vendor.name} ({vendor.count} products)
                  </Option>
                ))
              }
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="Filter by Category"
              style={{ width: '100%' }}
              size="large"
              allowClear
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              {stats && Object.entries(stats.categories)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, count]) => (
                  <Option key={category} value={category}>
                    {category} ({count})
                  </Option>
                ))
              }
            </Select>
          </Col>
        </Row>

        {searchMode === 'semantic' && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">
              <ThunderboltOutlined /> AI Semantic Search uses vector embeddings to find products
              that match your description, even if the exact words don't appear in the product.
              Try: "tools for employee training" or "invoice management software"
            </Text>
          </div>
        )}
      </Card>

      {/* Results Info */}
      {semanticResults && (
        <Card style={{ marginBottom: 16, background: '#f0f5ff' }}>
          <Text strong>
            <ThunderboltOutlined /> Semantic Search Results:
          </Text>
          <Text> Found {semanticResults.length} products matching "{searchQuery}"</Text>
          <Button
            type="link"
            onClick={() => {
              setSemanticResults(null);
              fetchProducts(selectedVendor, selectedCategory);
            }}
          >
            Clear semantic search
          </Button>
        </Card>
      )}

      {/* Products Table */}
      <Card>
        <Spin spinning={loading || searchLoading}>
          {displayData.length > 0 ? (
            <Table
              columns={displayColumns}
              dataSource={displayData}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`
              }}
              scroll={{ x: 1000 }}
            />
          ) : (
            <Empty
              description={
                <span>
                  No products found.
                  {searchQuery && " Try adjusting your search."}
                </span>
              }
            />
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default ScrapedProducts;
